import React from "react";
import { strings } from "../../../../../localization/strings";
import { Section } from "../../../decorations/Section";
import { ParameterItem } from "../../components/ParameterItem";
import { CheckProcessStageSectionOptionAccess } from "../../helpers/evisibility";
import { ProcessStages, IResultConfig, OpeningPreferences, validateUrl } from "@piceasoft/core";
import { CommandBar, ICommandBarItemProps, ICommandBarStyles, ITextField, Stack, TextField } from "@fluentui/react";
import { getRedirectUrlPlaceholderItems } from "../../../../../screens/services/Template/pivots/placeholdersSubMenuItems";
import { TransactionPlaceholders } from "../../../../../core/store/typings/TransactionPlaceholders";

type TProps = {
    config: IResultConfig
    onConfigCommit: (config: IResultConfig) => void
    channel: number
}

const openLinkPreferencesOptions = [
    { key: OpeningPreferences.SameBrowser, text: strings.CONSTRUCTOR.STAGES.SELF_SERVICE_PROMOTION.CURRENT_BROWSER_TAB },
    { key: OpeningPreferences.NewTab, text: strings.CONSTRUCTOR.STAGES.SELF_SERVICE_PROMOTION.NEW_BROWSER_TAB }
]

const validateRedirectUrl = (value: string): string | undefined => {
    const validationResult = validateUrl(value);

    if(validationResult.valid) {
        return undefined;
    }
        
    return strings.CONSTRUCTOR.STAGES.RESOURCES_CHECK.FORM.SOURCE_INVALID
}
   
export const ResultAdditionalSettingsPivot: React.FC<TProps> = ({ config, onConfigCommit, channel }) => {
    const redirectUrlTextFieldRef = React.useRef<ITextField>(null);
    const redirectUrlEnabled = config.completeFlowBehavior !== undefined;
    const redirectUrlValue = config.completeFlowBehavior?.redirectUrl ?? "";
   
    const renderPlaceholderMenu = (): JSX.Element => {

        return (
            <Stack horizontal verticalAlign="center" styles={{ root: { height: 32 } }}>
                <Stack.Item grow>
                    <CommandBar
                        items={_items}
                        styles={commandBarStyles}
                        overflowButtonProps={{ ariaLabel: 'More commands' }}
                        ariaLabel="Inbox actions"
                        primaryGroupAriaLabel="Main actions"
                        farItemsGroupAriaLabel="More actions"
                    />
                </Stack.Item>
            </Stack>
        )
    }

    const pastePlaceholder = (placeholder: TransactionPlaceholders | string) => {
        const selectionStart = redirectUrlTextFieldRef.current?.selectionStart;
        const selectionEnd = redirectUrlTextFieldRef.current?.selectionEnd ?? selectionStart;
        const insertAt = selectionStart ?? redirectUrlValue.length;
        const output = `${redirectUrlValue.substring(0, insertAt)}${placeholder}${redirectUrlValue.substring(selectionEnd ?? insertAt)}`;

        onConfigCommit({
            ...config,
            completeFlowBehavior: {
                ...config.completeFlowBehavior,
                redirectUrl: output
            }
        });
    }

    const _items: ICommandBarItemProps[] = [
        {
            key: 'placeholders',
            iconProps: { iconName: 'Variable2' },
            disabled: !redirectUrlEnabled,
            subMenuProps: {
                items: getRedirectUrlPlaceholderItems(pastePlaceholder)
            }
        },
    ]

    return (
        <Section max flat>
            <ParameterItem title={strings.CONSTRUCTOR.STAGES.RESULT.PIVOTS.ADDITIONAL.REDIRECT_URL.TITLE}
                useToggle
                noSeparator
                toggleEnabled={CheckProcessStageSectionOptionAccess(channel, ProcessStages.Result, 'Additional', 'Redirect to external url upon completion')}
                toggleChecked = {config.completeFlowBehavior !== undefined ? true : false}
                toggleOnChange={() => {
                    onConfigCommit({
                                    ...config,
                                    completeFlowBehavior: config.completeFlowBehavior ? undefined : {}
                                })
                }}
                description={strings.CONSTRUCTOR.STAGES.RESULT.PIVOTS.ADDITIONAL.REDIRECT_URL.DESCRIPTION}
            >
                <Stack horizontal verticalAlign="start" tokens={{ childrenGap: 4 }}>
                    <Stack.Item styles={{ root: { width: 320 } }}>
                        <TextField
                            componentRef={redirectUrlTextFieldRef}
                            disabled={!redirectUrlEnabled}
                            value={redirectUrlValue}
                            onGetErrorMessage={validateRedirectUrl}
                            validateOnLoad={false}
                            validateOnFocusOut
                            onChange={(ev, value) =>
                                onConfigCommit({
                                    ...config,
                                    completeFlowBehavior: {
                                        ...config.completeFlowBehavior,
                                        redirectUrl: value ?? ""
                                    }
                                })
                            }
                        />
                    </Stack.Item>
                    {renderPlaceholderMenu()}
                </Stack>
            </ParameterItem>
            { config.completeFlowBehavior && (       
                <ParameterItem title={strings.CONSTRUCTOR.STAGES.RESULT.PIVOTS.ADDITIONAL.OPEN_LINK_PREFERENCES.TITLE}
                    useToggle={false}
                    useDropdown
                    noSeparator
                    dropdownEnabled={CheckProcessStageSectionOptionAccess(channel, ProcessStages.Result, 'Additional', 'Link opening preferences')}
                    description={strings.CONSTRUCTOR.STAGES.RESULT.PIVOTS.ADDITIONAL.OPEN_LINK_PREFERENCES.DESCRIPTION}
                    dropdownOptions={openLinkPreferencesOptions}
                    dropdownSelectedKey={config.completeFlowBehavior?.openLinkPreference ?? OpeningPreferences.SameBrowser}
                    dropdownOnChange={(val) => onConfigCommit({
                        ...config,
                        completeFlowBehavior: { 
                            ...config.completeFlowBehavior,
                            openLinkPreference: val?.key as number
                        }
                    })}
                />
            )}
        </Section>
    )
}

const commandBarStyles: ICommandBarStyles = {
    root: {
        height: 32,
        margin: 0,
        padding: 0,
        selectors: {
            ".ms-CommandBar-primaryCommand": {
                height: 32,
            },
            ".ms-Button": {
                height: 32,
            },
        }
    }
};
